import { supabase } from '@/lib/supabase';

export type TipoAcao = 'criacao' | 'edicao' | 'eliminacao';

export type EntidadeAuditada = 'funcionario' | 'formacao' | 'participante' | 'documento' | 'formador' | 'utilizador' | 'unidade' | 'carreira';

export interface AlteracaoRegisto {
  id: string;
  entidade: EntidadeAuditada;
  entidade_id: string;
  entidade_nome: string;
  tipo_acao: TipoAcao;
  campo_alterado: string | null;
  valor_anterior: string | null;
  valor_novo: string | null;
  descricao: string;
  data_alteracao: string;
  municipio_id?: string | null;
  modulo?: string | null;
  user_email?: string | null;
}

export interface CampoAlterado {
  campo: string;
  anterior: string | null;
  novo: string | null;
}

function generateHistoricoId(): string {
  return 'ha_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
}

/**
 * Regista uma única alteração no histórico de alterações.
 * Função centralizada para ser usada por qualquer contexto.
 */
export async function registarAlteracao(
  entidade: EntidadeAuditada,
  entidadeId: string,
  entidadeNome: string,
  tipoAcao: TipoAcao,
  descricao: string,
  options?: {
    campoAlterado?: string;
    valorAnterior?: string | null;
    valorNovo?: string | null;
    municipioId?: string | null;
    modulo?: string | null;
    userEmail?: string | null;
  }
): Promise<void> {
  await supabase.from('historico_alteracoes').insert({
    id: generateHistoricoId(),
    entidade,
    entidade_id: entidadeId,
    entidade_nome: entidadeNome,
    tipo_acao: tipoAcao,
    campo_alterado: options?.campoAlterado ?? null,
    valor_anterior: options?.valorAnterior ?? null,
    valor_novo: options?.valorNovo ?? null,
    descricao,
    data_alteracao: new Date().toISOString(),
    municipio_id: options?.municipioId ?? null,
    modulo: options?.modulo ?? null,
    user_email: options?.userEmail ?? null,
  });
}

/**
 * Compara dois objetos e retorna uma lista de campos que mudaram.
 * Ignora campos com prefixo underscore e campos nulos vazios equivalentes.
 */
export function compararObjetos(
  anterior: Record<string, unknown>,
  novo: Record<string, unknown>,
  camposIgnorados: string[] = []
): CampoAlterado[] {
  const alterados: CampoAlterado[] = [];
  const todasChaves = new Set([...Object.keys(anterior), ...Object.keys(novo)]);

  for (const chave of todasChaves) {
    if (camposIgnorados.includes(chave)) continue;
    if (chave.startsWith('_')) continue;

    const valAnt = anterior[chave];
    const valNovo = novo[chave];

    // Normalizar para comparação
    const strAnt = valAnt === undefined || valAnt === null ? '' : String(valAnt);
    const strNovo = valNovo === undefined || valNovo === null ? '' : String(valNovo);

    if (strAnt !== strNovo) {
      alterados.push({
        campo: chave,
        anterior: strAnt || null,
        novo: strNovo || null,
      });
    }
  }

  return alterados;
}

/**
 * Nomes amigáveis para campos de funcionários.
 */
const LABELS_FUNCIONARIO: Record<string, string> = {
  nome: 'Nome',
  carreira: 'Carreira',
  sexo: 'Sexo',
  genero: 'Género',
  categoria: 'Categoria',
  ultima_progressao: 'Última Progressão',
  nivel_atual: 'Nível Atual',
  novo_nivel_2025: 'Novo Nível 2025',
  descricao_subida: 'Descrição da Subida',
  avaliacao: 'Avaliação',
  vencimento: 'Vencimento',
  unidade_organica: 'Unidade Orgânica',
  subunidade_organica: 'Subunidade',
  pontos_atuais: 'Pontos Atuais',
  pontos_descontados: 'Pontos Descontados',
  data_entrada_funcao_publica: 'Data Entrada FP',
  excecao_prepav: 'Exceção PREPAV',
  data_alteracao_categoria: 'Data Alteração Categoria',
  categoria_anterior: 'Categoria Anterior',
};

/**
 * Nomes amigáveis para campos de formações.
 */
const LABELS_FORMACAO: Record<string, string> = {
  nome: 'Nome da Formação',
  entidade_formadora: 'Entidade Formadora',
  categoria: 'Categoria',
  modalidade: 'Modalidade',
  data_inicio: 'Data de Início',
  data_fim: 'Data de Fim',
  duracao_horas: 'Duração (horas)',
  local: 'Local',
  descricao: 'Descrição',
  estado: 'Estado',
  custo: 'Custo',
  orcamento_previsto: 'Orçamento Previsto',
  financiamento: 'Financiamento',
  observacoes: 'Observações',
  formador_interno_id: 'Formador Interno',
};

/**
 * Nomes amigáveis para campos de participantes.
 */
const LABELS_PARTICIPANTE: Record<string, string> = {
  funcionario_id: 'Funcionário',
  funcionario_nome: 'Nome do Funcionário',
  estado_participacao: 'Estado de Participação',
  nota_final: 'Nota Final',
  certificado_emitido: 'Certificado Emitido',
  data_certificado: 'Data do Certificado',
  observacoes: 'Observações',
  avaliacao_nota: 'Nota de Avaliação',
  avaliacao_comentario: 'Comentário',
  avaliacao_data: 'Data da Avaliação',
  certificado_url: 'URL do Certificado',
  custo: 'Custo',
};

const LABELS_POR_ENTIDADE: Record<EntidadeAuditada, Record<string, string>> = {
  funcionario: LABELS_FUNCIONARIO,
  formacao: LABELS_FORMACAO,
  participante: LABELS_PARTICIPANTE,
  documento: {},
  formador: {},
  utilizador: {},
  unidade: {},
  carreira: {},
};

/**
 * Obtém o nome amigável de um campo para apresentação.
 */
export function labelCampo(entidade: EntidadeAuditada, campo: string): string {
  const map = LABELS_POR_ENTIDADE[entidade] ?? {};
  return map[campo] ?? campo;
}

/**
 * Regista alterações campo a campo.
 * Recebe o objeto anterior e o novo, compara-os e regista cada diferença individualmente.
 */
export async function registarAlteracaoDetalhada(
  entidade: EntidadeAuditada,
  entidadeId: string,
  entidadeNome: string,
  tipoAcao: TipoAcao,
  anterior: Record<string, unknown>,
  novo: Record<string, unknown>,
  options?: {
    municipioId?: string | null;
    modulo?: string | null;
    userEmail?: string | null;
    camposIgnorados?: string[];
  }
): Promise<CampoAlterado[]> {
  const diffs = compararObjetos(anterior, novo, options?.camposIgnorados ?? ['id', 'created_at', 'updated_at', 'municipio_id']);

  if (diffs.length === 0) return [];

  // Regista um registo resumo + um registo por campo alterado
  const now = new Date().toISOString();

  // Registo resumo
  await supabase.from('historico_alteracoes').insert({
    id: generateHistoricoId(),
    entidade,
    entidade_id: entidadeId,
    entidade_nome: entidadeNome,
    tipo_acao: tipoAcao,
    campo_alterado: null,
    valor_anterior: null,
    valor_novo: null,
    descricao: `${diffs.length} campo(s) alterado(s) em ${entidadeNome}.`,
    data_alteracao: now,
    municipio_id: options?.municipioId ?? null,
    modulo: options?.modulo ?? null,
    user_email: options?.userEmail ?? null,
  });

  // Registo individual por campo
  for (const diff of diffs) {
    await supabase.from('historico_alteracoes').insert({
      id: generateHistoricoId(),
      entidade,
      entidade_id: entidadeId,
      entidade_nome: entidadeNome,
      tipo_acao: tipoAcao,
      campo_alterado: labelCampo(entidade, diff.campo),
      valor_anterior: diff.anterior,
      valor_novo: diff.novo,
      descricao: `${labelCampo(entidade, diff.campo)} alterado em ${entidadeNome}`,
      data_alteracao: now,
      municipio_id: options?.municipioId ?? null,
      modulo: options?.modulo ?? null,
      user_email: options?.userEmail ?? null,
    });
  }

  return diffs;
}

/**
 * Obtém as últimas alterações do histórico, unificando todos os módulos.
 */
export async function obterAtividadeRecente(
  limite: number = 50,
  dias: number = 7
): Promise<AlteracaoRegisto[]> {
  const desde = new Date();
  desde.setDate(desde.getDate() - dias);

  const { data, error } = await supabase
    .from('historico_alteracoes')
    .select('*')
    .gte('data_alteracao', desde.toISOString())
    .order('data_alteracao', { ascending: false })
    .limit(limite);

  if (error) {
    console.error('[auditoria] Erro ao obter atividade recente:', error.message);
    return [];
  }

  return (data ?? []).map((row) => ({
    id: row.id as string,
    entidade: (row.entidade as EntidadeAuditada) ?? 'funcionario',
    entidade_id: row.entidade_id as string,
    entidade_nome: row.entidade_nome as string,
    tipo_acao: row.tipo_acao as TipoAcao,
    campo_alterado: row.campo_alterado as string | null,
    valor_anterior: row.valor_anterior as string | null,
    valor_novo: row.valor_novo as string | null,
    descricao: row.descricao as string,
    data_alteracao: row.data_alteracao as string,
    municipio_id: row.municipio_id as string | null,
    modulo: row.modulo as string | null,
    user_email: row.user_email as string | null,
  }));
}